/* eslint-disable react-hooks/exhaustive-deps */
import { FilterSettings } from './filter.Settings';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveAll, saveMany } from '../../store/items';
import { setOpenModales } from '../../store/global';
import { FilterLink } from './filter.Link';
import { TimelineAll } from '../timeline/timeline.All';
import { update } from '../../services/item';
import { deepCloneArray } from '../../utils/array';
import parse from 'html-react-parser';
import { ezMarkdownParser } from '../../utils/parserMarkdown/markdownEngine';
import { mdRules } from '../../content/mardownRules';
import { canEditItemForItem, getItemPicAsset, getId } from '../../utils/item';
import { syncAutoTabsOnLinkAdded, syncAutoTabsOnLinkRemoved } from '../../utils/item';
import useItems from '../../hooks/useItems';
import { FaPlus } from 'react-icons/fa';
import { LinkNew } from '../link/link.New';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { BoardReadOnlyContext } from '../../context/boardReadOnly';

export const FilterAll = props => {
  const {
    itemId,
    filterId,
    filter,
    displayType,
    canEdit: canEditProp,
    boardTabId,
    disableItemDrag = false,
  } = props;

  const [filteredItems, setFilteredItems] = useState([]);
  const filterItems = useItems(filter?.items);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepth = useRef(0);

  const items = useSelector(state => state.items);
  const item = useSelector(state => state.items[itemId]);
  const currentTab = useSelector(state => state.global.currentTab);
  const widgetTabId = boardTabId || currentTab;
  const tab = useSelector(state => state.items?.[widgetTabId]);
  const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
  const boardReadOnlyMode = !canMj ? tab?.readOnlyMode || null : null;
  const widgetConfig = useMemo(() => {
    if (!tab?.grid) {
      return {};
    }
    const entry = tab.grid.find(element => getId(element) === filterId);
    return entry?.[filterId] || {};
  }, [tab?.grid, filterId]);
  const simpleGapSize =
    widgetConfig?.simpleGapSize === 'sm' ||
    widgetConfig?.simpleGapSize === 'md' ||
    widgetConfig?.simpleGapSize === 'lg'
      ? widgetConfig.simpleGapSize
      : 'lg';
  const simpleGapClass =
    simpleGapSize === 'sm' ? 'gap-0' : simpleGapSize === 'md' ? 'gap-1' : 'gap-2';
  const isFilterDragLocked =
    disableItemDrag || widgetConfig?.filterDragLocked === true;
  const canEdit = canEditProp ?? canEditItemForItem(item, items);
  const dispatch = useDispatch();

  const displayTypeLink = displayType === 'massonery' ? 'réduit' : displayType;
  const isTimelineDisplay = displayType?.startsWith('timeline');
  const timelineMode =
    displayType === 'timeline vertical' ? 'vertical' : 'horizontal';
  const noteNumEnabled = filter?.noteNumEnabled === true;
  const noteNumPosition = filter?.noteNumPosition || 'top-right';
  const showAddItemButton = filter?.showAddItemButton === true;
  const noteNums =
    filter?.noteNums && typeof filter.noteNums === 'object' ? filter.noteNums : {};
  const inflightFetchKeyRef = useRef(null);
  const lastCompletedFetchKeyRef = useRef(null);

  const filterRefreshKey = useMemo(() => {
    if (!Array.isArray(filterItems) || filterItems.length === 0) {
      return '';
    }
    return filterItems
      .map(filterItem => {
        const id = filterItem?._id || 'unknown';
        const linksCount = Array.isArray(filterItem?.links)
          ? filterItem.links.length
          : 0;
        return `${id}:${linksCount}`;
      })
      .join('|');
  }, [filterItems]);

  const getFilterItems = useCallback(async (options = {}) => {
    const force = options?.force === true;
    const itemsParam = Array.isArray(filter?.items) ? filter.items.join(',') : '';
    const excludeParam = Array.isArray(filter?.exclude)
      ? filter.exclude.join(',')
      : '';
    const requestKey = `${itemsParam}|${excludeParam}|${filterRefreshKey}`;
    if (!force && lastCompletedFetchKeyRef.current === requestKey) {
      return;
    }
    if (inflightFetchKeyRef.current === requestKey) {
      return;
    }
    inflightFetchKeyRef.current = requestKey;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/items/filter`,
        {
          params: {
            items: itemsParam,
            exclude: excludeParam,
          },
        },
      );
      const baseItems = Array.isArray(response.data) ? response.data : [];
      const ids = Array.from(
        new Set(baseItems.map(resItem => resItem?._id).filter(Boolean)),
      );

      const missingIds = ids.filter(id => {
        const existing = items?.[id];
        return !existing || existing.__partial === true;
      });

      let fullItemsById = new Map();
      if (missingIds.length > 0) {
        try {
          const batchRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/items/batch`,
            { ids: missingIds },
          );
          if (Array.isArray(batchRes.data)) {
            fullItemsById = new Map(
              batchRes.data
                .filter(batchItem => batchItem?._id)
                .map(batchItem => [batchItem._id, batchItem]),
            );
          }
        } catch (e) {
          // Fallback to /items/filter payload if batch fails.
        }
      }

      const resolvedItems = baseItems.map(baseItem => {
        const fullItem = fullItemsById.get(baseItem?._id);
        return fullItem || baseItem;
      });

      if (resolvedItems.length > 0) {
        dispatch(saveMany({ items: resolvedItems, log: 'filter-all-batch' }));
      }
      setFilteredItems(resolvedItems);
      lastCompletedFetchKeyRef.current = requestKey;
    } catch (error) {
      setFilteredItems([]);
      console.error('Error fetching filter items:', error);
    } finally {
      if (inflightFetchKeyRef.current === requestKey) {
        inflightFetchKeyRef.current = null;
      }
    }
  }, [filter?.items, filter?.exclude, filterRefreshKey, items, dispatch]);

  useEffect(() => {
    if (filter?.items?.length > 0) {
      const timeoutId = setTimeout(() => {
        getFilterItems();
      }, 120);
      return () => clearTimeout(timeoutId);
    }
  }, [filter?.items, filter?.exclude, filterRefreshKey, getFilterItems]);

  useEffect(() => {
    const handler = event => {
      const refreshFilterId = event?.detail?.filterId;
      if (refreshFilterId && refreshFilterId === filterId) {
        getFilterItems({ force: true });
      }
    };
    window.addEventListener('filter-dnd-refresh', handler);
    return () => window.removeEventListener('filter-dnd-refresh', handler);
  }, [filterId, getFilterItems]);

  useEffect(() => {
    if (!isFilterDragLocked) {
      return;
    }
    dragDepth.current = 0;
    setIsDragOver(false);
  }, [isFilterDragLocked]);

  const sortedFilteredItems = useMemo(() => {
    if (!isTimelineDisplay) {
      return filteredItems;
    }
    return [...filteredItems].sort((a, b) =>
      (a?.label || '').localeCompare(b?.label || '', undefined, {
        sensitivity: 'base',
      }),
    );
  }, [filteredItems, isTimelineDisplay]);

  const timelinePoints = useMemo(() => {
    if (!isTimelineDisplay) {
      return [];
    }
    const extractNumericValue = label => {
      if (!label) return null;
      const match = label.match(/-?\d+(\.\d+)?/);
      if (!match) return null;
      const value = Number(match[0]);
      return Number.isFinite(value) ? value : null;
    };
    return sortedFilteredItems.map((item, index) => {
      const avatarAsset = getItemPicAsset(item, 0);
      const numericValue = extractNumericValue(item?.label);
      return {
        id: `${filterId}-${item._id}`,
        value: numericValue ?? index + 1,
        itemLabel: item?.label || 'Sans nom',
        itemId: item?._id,
        numId: null,
        picUrl: avatarAsset.url,
        picIconSlug: avatarAsset.iconSlug,
        color: item?.color,
        entryIndex: index,
      };
    });
  }, [sortedFilteredItems, isTimelineDisplay, filterId]);

  const emptyLabelNode = useMemo(() => {
    if (!filter?.emptyLabel) {
      return null;
    }
    let htmlValue = ezMarkdownParser(filter.emptyLabel);
    for (let i = 0; i < mdRules.length; i++) {
      const rule = mdRules[i];
      htmlValue = htmlValue.replace(rule.pattern, rule.replacement);
    }
    return (
      <div className='text-center text-contrast text-opacity-60'>
        {parse(htmlValue)}
      </div>
    );
  }, [filter?.emptyLabel]);

  if (!filter) return null;

  const resolveItemsByIds = async (ids, forceIds = []) => {
    const resolved = new Map();
    const uniqueIds = Array.from(
      new Set(ids.filter(id => typeof id === 'string' && id.length > 0)),
    );
    uniqueIds.forEach(id => {
      const existing = items?.[id];
      if (existing && existing.__partial !== true) {
        resolved.set(id, existing);
      }
    });
    const forceSet = new Set(
      forceIds.filter(id => typeof id === 'string' && id.length > 0),
    );
    const missing = uniqueIds.filter(
      id => !resolved.has(id) || forceSet.has(id),
    );
    if (missing.length > 0) {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missing },
        );
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (item?._id) {
              resolved.set(item._id, item);
              const existing = items?.[item._id];
              if (!existing || existing.__partial) {
                dispatch(saveAll({ item, log: 'filter-dnd' }));
              }
            }
          });
        }
      } catch (e) {
        // ignore
      }
    }
    return resolved;
  };

  const getFilterConfigById = targetFilterId => {
    if (!item?.filters || !targetFilterId) {
      return null;
    }
    const entry = item.filters.find(
      filterObj => getId(filterObj) === targetFilterId,
    );
    return entry ? entry[targetFilterId] : null;
  };

  const applyLinks = async (draggedId, targetIds) => {
    if (!draggedId || targetIds.length === 0) {
      return;
    }
    const resolveIds = [draggedId, ...targetIds];
    const resolved = await resolveItemsByIds(resolveIds, resolveIds);
    const draggedItem = resolved.get(draggedId);
    if (!draggedItem) {
      return;
    }
    const draggedLinks = Array.isArray(draggedItem.links)
      ? deepCloneArray(draggedItem.links)
      : [];
    const existingDragged = new Set(
      draggedLinks.map(linkObj => getId(linkObj)),
    );
    let draggedChanged = false;
    targetIds.forEach(targetId => {
      if (!targetId || targetId === draggedId) return;
      if (!existingDragged.has(targetId)) {
        draggedLinks.push({
          [targetId]: { id: targetId, x: 0, y: 0, w: 4, h: 1, i: targetId },
        });
        existingDragged.add(targetId);
        draggedChanged = true;
      }
    });
    const tasks = [];
    if (draggedChanged) {
      tasks.push(update(`items/${draggedId}`, 'links', draggedLinks));
    }
    targetIds.forEach(targetId => {
      if (!targetId || targetId === draggedId) return;
      const targetItem = resolved.get(targetId);
      if (!targetItem) return;
      const targetLinks = Array.isArray(targetItem.links)
        ? deepCloneArray(targetItem.links)
        : [];
      const targetExisting = new Set(
        targetLinks.map(linkObj => getId(linkObj)),
      );
      if (!targetExisting.has(draggedId)) {
        targetLinks.push({
          [draggedId]: { id: draggedId, x: 0, y: 0, w: 4, h: 1, i: draggedId },
        });
        tasks.push(update(`items/${targetId}`, 'links', targetLinks));
      }
    });
    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
    const draggedRef = resolved.get(draggedId);
    await Promise.all(
      targetIds.map(async targetId => {
        const targetRef = resolved.get(targetId);
        if (draggedRef && targetRef && targetId !== draggedId) {
          await syncAutoTabsOnLinkAdded(draggedRef, targetRef);
        }
      }),
    );
  };

  const removeLinks = async (draggedId, targetIds) => {
    if (!draggedId || targetIds.length === 0) {
      return;
    }
    const resolveIds = [draggedId, ...targetIds];
    const resolved = await resolveItemsByIds(resolveIds, resolveIds);
    const draggedItem = resolved.get(draggedId);
    if (!draggedItem) {
      return;
    }
    const targetSet = new Set(targetIds.filter(id => id && id !== draggedId));
    const draggedLinks = Array.isArray(draggedItem.links)
      ? deepCloneArray(draggedItem.links)
      : [];
    const nextDraggedLinks = draggedLinks.filter(
      linkObj => !targetSet.has(getId(linkObj)),
    );
    const tasks = [];
    if (nextDraggedLinks.length !== draggedLinks.length) {
      tasks.push(update(`items/${draggedId}`, 'links', nextDraggedLinks));
    }
    targetIds.forEach(targetId => {
      if (!targetId || targetId === draggedId) return;
      const targetItem = resolved.get(targetId);
      if (!targetItem) return;
      const targetLinks = Array.isArray(targetItem.links)
        ? deepCloneArray(targetItem.links)
        : [];
      const nextTargetLinks = targetLinks.filter(
        linkObj => getId(linkObj) !== draggedId,
      );
      if (nextTargetLinks.length !== targetLinks.length) {
        tasks.push(update(`items/${targetId}`, 'links', nextTargetLinks));
      }
    });
    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
    const draggedRef = resolved.get(draggedId);
    await Promise.all(
      targetIds.map(async targetId => {
        const targetRef = resolved.get(targetId);
        if (draggedRef && targetRef && targetId !== draggedId) {
          await syncAutoTabsOnLinkRemoved(draggedRef, targetRef);
        }
      }),
    );
  };

  const handleDrop = async event => {
    if (!canEdit) return;
    event.preventDefault();
    setIsDragOver(false);
    dragDepth.current = 0;
    if (isFilterDragLocked) {
      window.__filterDropTargetId = filterId;
      return;
    }
    const raw = event.dataTransfer.getData('application/x-estynoss-item');
    if (!raw) return;
    let payload = null;
    try {
      payload = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!payload || payload.type !== 'item' || !payload.itemId) {
      return;
    }
    const draggedId = payload.itemId;
    window.__filterDropTargetId = filterId;
    if (payload.sourceFilterId && payload.sourceFilterId !== filterId) {
      const sourceConfig = getFilterConfigById(payload.sourceFilterId);
      const sourceIds = Array.isArray(sourceConfig?.items)
        ? sourceConfig.items
        : [];
      await removeLinks(draggedId, sourceIds);
      window.dispatchEvent(
        new CustomEvent('filter-dnd-refresh', {
          detail: { filterId: payload.sourceFilterId },
        }),
      );
    }
    const targetIds = Array.isArray(filter?.items) ? filter.items : [];
    if (targetIds.length === 0) {
      return;
    }
    await applyLinks(draggedId, targetIds);
    getFilterItems();
  };

  const handleDragOut = async (draggedId, sourceFilterId) => {
    if (!canEdit || isFilterDragLocked) return;
    if (!draggedId || !sourceFilterId) return;
    if (window.__filterDropTargetId) return;
    const sourceConfig = getFilterConfigById(sourceFilterId);
    const sourceIds = Array.isArray(sourceConfig?.items)
      ? sourceConfig.items
      : [];
    await removeLinks(draggedId, sourceIds);
    getFilterItems();
  };

  const updateNoteNum = (targetItemId, nextValue) => {
    if (!canEdit) return;
    if (!item?.filters) return;
    if (!targetItemId) return;

    const numValue = Number(nextValue);
    if (!Number.isFinite(numValue)) return;

    const clonedFilters = deepCloneArray(item.filters);
    const filterIdx = clonedFilters.findIndex(
      filterObj => getId(filterObj) === filterId,
    );
    if (filterIdx === -1) return;

    const current = clonedFilters[filterIdx]?.[filterId] || {};
    const nextMap =
      current.noteNums && typeof current.noteNums === 'object'
        ? { ...current.noteNums }
        : {};
    nextMap[targetItemId] = Math.max(0, Math.round(numValue));

    clonedFilters[filterIdx][filterId] = { ...current, noteNums: nextMap };
    update(`items/${itemId}`, 'filters', clonedFilters);
  };

  const applyFilterLinksToItem = async targetItem => {
    const targetId = targetItem?._id;
    if (!targetId) return;
    const requiredIds = Array.isArray(filter?.items) ? filter.items : [];
    const excludedIds = Array.isArray(filter?.exclude) ? filter.exclude : [];
    if (requiredIds.length > 0) {
      await applyLinks(targetId, requiredIds);
    }
    if (excludedIds.length > 0) {
      await removeLinks(targetId, excludedIds);
    }
    getFilterItems();
  };

  return (
    <BoardReadOnlyContext.Provider value={boardReadOnlyMode}>
      <div
        data-testid='filter-drop-zone'
        className={`relative h-full overflow-auto flex-wrap flex ${
          displayType === 'réduit' || filteredItems.length === 0
            ? 'items-center justify-center'
            : ''
        }`}
        onDragOver={event => {
          if (!canEdit) return;
          event.preventDefault();
          if (isFilterDragLocked && event.dataTransfer) {
            event.dataTransfer.dropEffect = 'none';
          }
        }}
        onDragEnter={() => {
          if (!canEdit) return;
          dragDepth.current += 1;
          if (isFilterDragLocked) {
            window.__filterDropTargetId = filterId;
            return;
          }
          setIsDragOver(true);
        }}
        onDragLeave={event => {
          if (!canEdit) return;
          const nextTarget = event.relatedTarget;
          if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
            dragDepth.current = 0;
            setIsDragOver(false);
            if (
              isFilterDragLocked &&
              window.__filterDropTargetId === filterId
            ) {
              window.__filterDropTargetId = null;
            }
            return;
          }
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) {
            setIsDragOver(false);
            if (
              isFilterDragLocked &&
              window.__filterDropTargetId === filterId
            ) {
              window.__filterDropTargetId = null;
            }
          }
        }}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className='pointer-events-none absolute inset-0 z-20 rounded border-2 border-dashed border-secondary bg-secondary bg-opacity-10' />
        )}
        <>
        {canEdit && (
          <FilterSettings
            itemId={itemId}
            filterId={filterId}
            filter={filter}
            displayType={displayType}
            boardTabId={widgetTabId}
          />
        )}
        {canEdit && (
          <LinkNew
            itemId={itemId}
            mode='filter'
            modalId={`addFilterItem-${filterId}-${itemId}`}
            title={`Ajouter un item à ${filter?.label || 'ce filtre'}`}
            onHandleExistingItem={applyFilterLinksToItem}
            onHandleCreatedItem={applyFilterLinksToItem}
          />
        )}
        {canEdit && showAddItemButton && (
          <div className='w-full flex justify-end p-05'>
            <div
              className='board-grid-no-drag rounded-full w-2 h-2 flex items-center justify-center bg-primary text-main shadow cursor-pointer transition hover:opacity-80'
              onClick={() =>
                dispatch(
                  setOpenModales({
                    id: `addFilterItem-${filterId}-${itemId}`,
                    open: true,
                  }),
                )
              }
              title='Créer / ajouter un item'
            >
              <FaPlus />
            </div>
          </div>
        )}
        {filter?.showItemsLabel === true && (
          <div className='w-full px-1 py-05 text-xs text-contrast text-opacity-70'>
            {filterItems?.length > 0 ? (
              <div className='flex flex-wrap gap-05'>
                {filterItems.map(filterItem => (
                  <div
                    key={filterItem?._id || filterItem?.label}
                    className='bg-main bg-opacity-40 px-05 py-02 rounded'
                  >
                    {filterItem?.label || 'sans nom'}
                  </div>
                ))}
              </div>
            ) : (
              <div className='opacity-60'>Aucun item lié</div>
            )}
          </div>
        )}
        {isTimelineDisplay ? (
          <TimelineAll
            order='asc'
            points={timelinePoints}
            emptyLabel={
              emptyLabelNode || 'Aucun item ne correspond à ces filtres.'
            }
            showLabels={true}
            showValues={false}
            mode={timelineMode}
          />
        ) : (
          <div
            className={`${
              displayType === 'massonery'
                ? 'flex-1 p-1'
                : `flex flex-wrap justify-center w-full p-1 ${
                    displayType === 'simple' ? simpleGapClass : 'gap-2'
                  }`
            } 
        ${
          displayType === 'massonery'
            ? 'grid grid-rows-' +
              Math.max(filteredItems.length, 12) +
              ' grid-cols-4 gap-2'
            : ''
        }`}
          >
            {filteredItems.length > 0
              ? filteredItems?.map(item => (
                <FilterLink
                  key={item?._id}
                  item={item}
                  parentId={itemId}
                  displayTypeLink={displayTypeLink}
                  displayType={displayType}
                  elementsToDisplay={
                    widgetConfig?.elementsToDisplay || filter?.elementsToDisplay
                  }
                  itemsPerRow={
                    Number.isFinite(Number(widgetConfig?.itemsPerRow))
                      ? Number(widgetConfig.itemsPerRow)
                      : filter?.itemsPerRow || 5
                  }
                  fullTabId={
                    widgetConfig?.fullTabId || 'sidebar'
                  }
                  filterId={filterId}
                  canEdit={canEdit}
                  disableItemDrag={isFilterDragLocked}
                  onDragOut={handleDragOut}
                  noteNumEnabled={noteNumEnabled}
                  noteNumPosition={noteNumPosition}
                  noteNum={noteNums?.[item?._id]}
                  onChangeNoteNum={value => updateNoteNum(item?._id, value)}
                  itemStyleConfig={widgetConfig?.itemStyle || filter?.itemStyle}
                  reducedMinAvatarSize={
                    Number.isFinite(Number(widgetConfig?.reducedMinAvatarSize))
                      ? Number(widgetConfig.reducedMinAvatarSize)
                      : filter?.reducedMinAvatarSize
                  }
                />
                ))
              : emptyLabelNode || (
                  <div className='text-center text-contrast text-opacity-60'>
                    aucun item ne correspond à ces filtres
                  </div>
                )}
          </div>
        )}
        </>
      </div>
    </BoardReadOnlyContext.Provider>
  );
};
